import { useState, useEffect } from "react";
import { useListings } from "../../hooks/useListings";
import { useCategories } from "../../hooks/useCategories";
import { Search, Filter, FileText, Edit, Trash2, Eye } from "lucide-react";
import Card from "../components/Card";
import Table from "../components/Table";
import Button from "../components/Button";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";

const Posts = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    category_id: "",
    country: "",
  });
  const navigate = useNavigate();
  const {
    listings,
    loading,
    error,
    editListing,
    removeListing,
    getPaginatedListings,
  } = useListings({ page: currentPage, pagesize: pageSize, filters });
  const { categories } = useCategories();
  const { addToast } = useToast();
  const [filteredListings, setFilteredListings] = useState([]);
  const [displayedListings, setDisplayedListings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(loading);

  // Form state for post editing
  const [formData, setFormData] = useState({
    post_title: "",
    description: "",
    category_id: "",
    images: [],
    attributes: {},
    show_post_to_disabled_people: false,
    contact_email: "",
    relay_email: false,
    country: "",
    city: "",
    region: "",
    phone_number: "",
  });
  const token = localStorage.getItem("token");
  // Show error toast on fetch failure
  useEffect(() => {
    if (error) {
      addToast(
        error?.response?.data?.detail || "Failed to load posts",
        "error"
      );
    }
  }, [error]);
  // Filter and search logic
  useEffect(() => {
    let result = listings.data || [];
    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (post) =>
          post.post_title?.toLowerCase().includes(search) ||
          post.description?.toLowerCase().includes(search) ||
          (post.country && post.country.toLowerCase().includes(search))
      );
    }
    setFilteredListings(result);
    setTotalPages(listings.pagination?.pages || 1);
    setDisplayedListings(result);
  }, [listings, searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      let result = await getPaginatedListings(currentPage, pageSize, token);
      if (error) {
        navigate("/status", { state: { statusCode: error?.status } });
      } else if (result) {
        setTotalPages(result.pagination?.pages || 1);
        setDisplayedListings(result.data);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [currentPage, pageSize, error]);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already applied via useEffect
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setShowFilters(false);
  };

  const handleEditPost = (post) => {
    setSelectedPost(post);
    setFormData({
      post_title: post.post_title,
      description: post.description,
      category_id: post.category_id,
      images: post.images || [],
      attributes: post.attributes || {},
      show_post_to_disabled_people: post.show_post_to_disabled_people || false,
      contact_email: post.contact_email || "",
      relay_email: post.relay_email || false,
      country: post.country || "",
      city: post.city || "",
      region: post.region || "",
      phone_number: post.phone_number || "",
    });
    setIsEditModalOpen(true);
  };

  const handleViewPost = (post) => {
    setSelectedPost(post);
    setIsViewModalOpen(true);
  };

  const handleDeletePost = (post) => {
    setSelectedPost(post);
    setIsDeleteModalOpen(true);
  };

  // Save (edit) post
  const handleSavePost = async (e) => {
    e.preventDefault();
    if (!selectedPost) return;
    try {
      await editListing(selectedPost.id, formData, token);

      setIsEditModalOpen(false);
      addToast("Post updated successfully", "success");
    } catch (err) {
      addToast(err?.response?.data?.detail || "Failed to update post", "error");
    }
  };

  // Delete post
  const handleConfirmDelete = async () => {
    if (!selectedPost) return;
    try {
      await removeListing(selectedPost.id, token);
      setIsDeleteModalOpen(false);
      addToast("Post deleted successfully", "success");
    } catch (err) {
      addToast(err?.response?.data?.detail || "Failed to delete post", "error");
    }
  };

  // Get unique countries for filter dropdown
  const countries = Array.isArray(listings)
    ? [
        ...new Set(
          listings.filter((post) => post.country).map((post) => post.country)
        ),
      ].sort()
    : [];

  // Helper function to render JSON attributes
  const renderAttributes = (attributes) => {
    if (!attributes || Object.keys(attributes).length === 0) {
      return <span className="text-gray-500">No attributes</span>;
    }
    return (
      <div className="space-y-1">
        {Object.entries(attributes).map(([key, value]) => (
          <div key={key}>
            <span className="font-medium text-gray-700">{key}: </span>
            <span className="text-gray-600">
              {Array.isArray(value)
                ? value.join(", ")
                : typeof value === "object"
                ? JSON.stringify(value)
                : value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to get category name by id
  const getCategoryName = (categoryId) => {
    const category = (categories || []).find((c) => c.id === categoryId);
    return category ? category.name : "Unknown";
  };

  // Helper function to get image URLs (backend returns URLs directly)
  const getImageUrls = (imagesArr) => {
    if (!imagesArr || !imagesArr.length) return [];
    return imagesArr;
  };

  const columns = [
    {
      header: "#",
      accessor: "row_number",
      cell: (row) => (
        <span>
          {displayedListings.indexOf(row) + 1 + (currentPage - 1) * pageSize}
        </span>
      ),
    },
    {
      header: "Title",
      accessor: "post_title",
      cell: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.post_title}</div>
          <div className="text-sm text-gray-500 truncate max-w-xs">
            {row.description}
          </div>
        </div>
      ),
    },
    {
      header: "Category",
      accessor: "category_id",
      cell: (row) => (
        <Badge variant="blue">{getCategoryName(row.category_id)}</Badge>
      ),
    },
    {
      header: "Paid",
      accessor: "is_paid",
      cell: (row) => (
        <Badge variant={row.is_paid ? "green" : "red"}>
          {row.is_paid ? "Paid" : "Unpaid"}
        </Badge>
      ),
    },
    {
      header: "Deleted",
      accessor: "is_deleted",
      cell: (row) => (
        <Badge variant={row.is_deleted ? "red" : "green"}>
          {row.is_deleted ? "Deleted" : "Active"}
        </Badge>
      ),
    },
    {
      header: "Images",
      accessor: "images",
      cell: (row) => (
        <div className="flex items-center">
          {row.images && row.images.length > 0 ? (
            <div className="flex -space-x-2 overflow-hidden">
              {getImageUrls(row.images.slice(0, 3)).map((url, idx) => (
                <img
                  key={idx}
                  src={url || "/placeholder.svg"}
                  alt={`ImageIcon ${idx + 1}`}
                  className="h-8 w-8 rounded-full border border-white object-cover"
                />
              ))}
              {row.images.length > 3 && (
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-200 border border-white text-xs text-gray-500">
                  +{row.images.length - 3}
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-500">No images</span>
          )}
        </div>
      ),
    },
    {
      header: "Location",
      accessor: "country",
      cell: (row) => (
        <span className="text-gray-600">
          {row.city && row.country
            ? `${row.city}, ${row.country}`
            : row.country || "No location"}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              handleViewPost(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Edit}
            onClick={(e) => {
              e.stopPropagation();
              handleEditPost(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Trash2}
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePost(row);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage and moderate content posts
        </p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between mb-4">
          <form onSubmit={handleSearch} className="flex mb-4 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search posts..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={Filter}
              className="ml-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter
            </Button>
          </form>
          <Button icon={FileText}>New Post</Button>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ category_id: e.target.value || "" })
                  }
                  value={filters.category_id}
                >
                  <option value="">All Categories</option>
                  {(categories || []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  onChange={(e) =>
                    handleFilterChange({ country: e.target.value || "" })
                  }
                  value={filters.country}
                >
                  <option value="">All Countries</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center mb-4">
          <label className="mr-2 text-sm font-medium text-gray-700">
            Page Size:
          </label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="px-2 py-1 border border-gray-300 rounded-md"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={150}>150</option>
            <option value={200}>200</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
        <Table
          columns={columns}
          data={displayedListings}
          isLoading={isLoading}
          onRowClick={handleViewPost}
          emptyState="No posts found. Try adjusting your search or filters."
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </Card>

      {/* View Post Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="View Post"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => {
                setIsViewModalOpen(false);
                handleDeletePost(selectedPost);
              }}
            >
              Delete
            </Button>
            <div className="space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </Button>
              <Button
                icon={Edit}
                onClick={() => {
                  setIsViewModalOpen(false);
                  handleEditPost(selectedPost);
                }}
              >
                Edit
              </Button>
            </div>
          </div>
        }
      >
        {selectedPost && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {selectedPost.post_title}
              </h3>
              <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                <span>
                  Category: {getCategoryName(selectedPost.category_id)}
                </span>
                <span>•</span>
                <span>Post ID: {selectedPost.id}</span>
                <span>•</span>
                <span>User ID: {selectedPost.user_id}</span>
                <span>•</span>
                <span>
                  Email: {selectedPost?.user?.email || "No email provided"}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Description
              </h4>
              <p className="text-gray-700">{selectedPost.description}</p>
            </div>

            {selectedPost.images && selectedPost.images.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Images
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {getImageUrls(selectedPost.images).map((url, idx) => (
                    <div
                      key={idx}
                      className="aspect-square bg-gray-100 rounded-md overflow-hidden"
                    >
                      <img
                        src={url || "/placeholder.svg"}
                        alt={`ImageIcon ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  ImageIcon URLs: {selectedPost.images.join(", ")}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Attributes
              </h4>
              {renderAttributes(selectedPost.attributes)}
            </div>

            <div className="border-t border-gray-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Location
                </h4>
                <p className="text-gray-700">
                  {selectedPost.city &&
                  selectedPost.region &&
                  selectedPost.country
                    ? `${selectedPost.city}, ${selectedPost.region}, ${selectedPost.country}`
                    : selectedPost.city && selectedPost.country
                    ? `${selectedPost.city}, ${selectedPost.country}`
                    : selectedPost.country || "No location information"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Contact Information
                </h4>
                <p className="text-gray-700">
                  {selectedPost.contact_email && (
                    <div className="flex items-center">
                      <span className="font-medium">Email:</span>
                      <span className="ml-2">{selectedPost.contact_email}</span>
                      <Badge
                        variant={selectedPost.relay_email ? "blue" : "gray"}
                        className="ml-2"
                      >
                        {selectedPost.relay_email
                          ? "Relay Email"
                          : "Direct Email"}
                      </Badge>
                    </div>
                  )}
                  {selectedPost.phone_number && (
                    <div className="flex items-center mt-1">
                      <span className="font-medium">Phone:</span>
                      <span className="ml-2">{selectedPost.phone_number}</span>
                    </div>
                  )}
                  {!selectedPost.contact_email &&
                    !selectedPost.phone_number &&
                    "No contact information"}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Privacy Settings
              </h4>
              <div className="flex items-center">
                <input
                  id="view-show-disabled"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={selectedPost.show_post_to_disabled_people || false}
                  readOnly
                  disabled
                />
                <label
                  htmlFor="view-show-disabled"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Show post to disabled people
                </label>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Post Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Post"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button className="ml-3" onClick={handleSavePost}>
              Save Changes
            </Button>
          </>
        }
      >
        <form onSubmit={handleSavePost}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.post_title}
                onChange={(e) =>
                  setFormData({ ...formData, post_title: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.category_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category_id: Number(e.target.value),
                  })
                }
              >
                <option value="">Select Category</option>
                {(categories || []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}{" "}
                    {category.price ? `($${category.price})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Images editing is not supported for backend URLs, so just show URLs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Images
              </label>
              <div className="border border-gray-300 rounded-md p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {getImageUrls(formData.images).map((url, idx) => (
                    <div
                      key={idx}
                      className="aspect-square bg-gray-100 rounded-md overflow-hidden relative"
                    >
                      <img
                        src={url || "/placeholder.svg"}
                        alt={`ImageIcon ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-500">
                  Image URLs:{" "}
                  {formData.images.length > 0
                    ? formData.images.join(", ")
                    : "None"}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attributes (JSON)
              </label>
              <textarea
                rows={5}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm font-mono text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={JSON.stringify(formData.attributes, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({ ...formData, attributes: parsed });
                  } catch (error) {
                    // Allow invalid JSON during editing, but don't update the state
                  }
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter valid JSON for attributes
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.contact_email}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_email: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.phone_number}
                  onChange={(e) =>
                    setFormData({ ...formData, phone_number: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.country}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.region}
                  onChange={(e) =>
                    setFormData({ ...formData, region: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="relay_email"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.relay_email}
                  onChange={(e) =>
                    setFormData({ ...formData, relay_email: e.target.checked })
                  }
                />
                <label
                  htmlFor="relay_email"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Relay Email (Hide actual email address)
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="show_post_to_disabled_people"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.show_post_to_disabled_people}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      show_post_to_disabled_people: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="show_post_to_disabled_people"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Show post to disabled people
                </label>
              </div>
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Post Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Post"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ml-3"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        {selectedPost && (
          <p className="text-sm text-gray-500">
            Are you sure you want to delete the post "{selectedPost.post_title}"
            ? This action cannot be undone.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default Posts;
